import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

type ToastType = 'success' | 'error' | 'warning' | 'info';

interface ToastOptions {
  id: string;
  type: ToastType;
  title?: string;
  message: string;
}

interface ShowToastOptions {
  type?: ToastType;
  title?: string;
  message: string;
  duration?: number;
}

interface ConfirmOptions {
  title?: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  tone?: 'default' | 'danger';
}

interface FeedbackContextValue {
  showToast: (options: ShowToastOptions) => void;
  showSuccess: (message: string, title?: string) => void;
  showError: (message: string, title?: string) => void;
  showWarning: (message: string, title?: string) => void;
  showInfo: (message: string, title?: string) => void;
  confirm: (options: ConfirmOptions) => Promise<boolean>;
  dismissToast: (id: string) => void;
}

interface ConfirmState extends ConfirmOptions {
  resolver: (result: boolean) => void;
}

const FeedbackContext = createContext<FeedbackContextValue | undefined>(undefined);

const buildToastId = () => `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

export const FeedbackProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [toasts, setToasts] = useState<ToastOptions[]>([]);
  const [confirmState, setConfirmState] = useState<ConfirmState | null>(null);
  const timersRef = useRef<Record<string, ReturnType<typeof setTimeout>>>({});

  const dismissToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
    if (timersRef.current[id]) {
      clearTimeout(timersRef.current[id]);
      delete timersRef.current[id];
    }
  }, []);

  const showToast = useCallback((options: ShowToastOptions) => {
    const id = buildToastId();
    const toast: ToastOptions = {
      id,
      type: options.type || 'info',
      title: options.title,
      message: options.message
    };

    setToasts((prev) => [...prev, toast]);

    const duration = options.duration ?? 4500;
    if (duration > 0) {
      timersRef.current[id] = setTimeout(() => dismissToast(id), duration);
    }
  }, [dismissToast]);

  const showSuccess = useCallback((message: string, title?: string) => {
    showToast({ type: 'success', message, title });
  }, [showToast]);

  const showError = useCallback((message: string, title?: string) => {
    showToast({ type: 'error', message, title });
  }, [showToast]);

  const showWarning = useCallback((message: string, title?: string) => {
    showToast({ type: 'warning', message, title });
  }, [showToast]);

  const showInfo = useCallback((message: string, title?: string) => {
    showToast({ type: 'info', message, title });
  }, [showToast]);

  const confirm = useCallback((options: ConfirmOptions) => {
    return new Promise<boolean>((resolve) => {
      setConfirmState({
        ...options,
        resolver: resolve
      });
    });
  }, []);

  const handleConfirm = useCallback((result: boolean) => {
    if (confirmState) {
      confirmState.resolver(result);
      setConfirmState(null);
    }
  }, [confirmState]);

  const value = useMemo<FeedbackContextValue>(() => ({
    showToast,
    showSuccess,
    showError,
    showWarning,
    showInfo,
    confirm,
    dismissToast
  }), [
    showToast,
    showSuccess,
    showError,
    showWarning,
    showInfo,
    confirm,
    dismissToast
  ]);

  useEffect(() => {
    const timers = timersRef.current;
    return () => {
      Object.values(timers).forEach(clearTimeout);
    };
  }, []);

  const getToastAlertClass = (type: ToastType): string => {
    switch (type) {
      case 'success':
        return 'alert-success';
      case 'error':
        return 'alert-error';
      case 'warning':
        return 'alert-warning';
      case 'info':
        return 'alert-info';
      default:
        return '';
    }
  };

  const getToastIcon = (type: ToastType): React.ReactNode => {
    const iconProps = {
      className: 'h-6 w-6 flex-shrink-0',
      fill: 'none',
      viewBox: '0 0 24 24',
      strokeWidth: 2,
      stroke: 'currentColor'
    };

    switch (type) {
      case 'success':
        return (
          <svg {...iconProps} xmlns="http://www.w3.org/2000/svg">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
        );
      case 'error':
        return (
          <svg {...iconProps} xmlns="http://www.w3.org/2000/svg">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
        );
      case 'warning':
        return (
          <svg {...iconProps} xmlns="http://www.w3.org/2000/svg">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
            />
          </svg>
        );
      case 'info':
        return (
          <svg {...iconProps} xmlns="http://www.w3.org/2000/svg">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
        );
      default:
        return null;
    }
  };

  return (
    <FeedbackContext.Provider value={value}>
      {children}
      
      {/* Container de Toasts */}
      <div className="fixed top-4 right-4 left-4 md:left-auto z-50 flex flex-col gap-2 w-auto md:max-w-md pointer-events-none">
        {toasts.map((toast) => (
          <div
            key={toast.id}
            className={`alert ${getToastAlertClass(toast.type)} shadow-lg pointer-events-auto animate-fade-in min-w-0`}
          >
            <div className="flex items-start gap-3 flex-1 min-w-0">
              <div className="shrink-0 mt-0.5">{getToastIcon(toast.type)}</div>
              <div className="flex-1 min-w-0">
                {toast.title && (
                  <div className="font-bold text-sm mb-1 wrap-break-word">{toast.title}</div>
                )}
                <div className="text-sm wrap-break-word">{toast.message}</div>
              </div>
            </div>
            <button
              type="button"
              className="btn btn-sm btn-ghost btn-circle shrink-0"
              onClick={() => dismissToast(toast.id)}
              aria-label="Fechar"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-5 w-5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>
        ))}
      </div>

      {/* Modal de Confirmação */}
      {confirmState && (
        <div className="modal modal-open">
          <div className="modal-box">
            {confirmState.title && (
              <h3 className="font-bold text-lg mb-2">{confirmState.title}</h3>
            )}
            <p className="py-4">{confirmState.message}</p>
            <div className="modal-action">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => handleConfirm(false)}
              >
                {confirmState.cancelText || 'Cancelar'}
              </button>
              <button
                type="button"
                className={`btn ${confirmState.tone === 'danger' ? 'btn-error' : 'btn-primary'}`}
                onClick={() => handleConfirm(true)}
              >
                {confirmState.confirmText || 'Confirmar'}
              </button>
            </div>
          </div>
          <div className="modal-backdrop" onClick={() => handleConfirm(false)}></div>
        </div>
      )}
    </FeedbackContext.Provider>
  );
};

export const useFeedback = () => {
  const context = useContext(FeedbackContext);
  if (!context) {
    throw new Error('useFeedback deve ser utilizado dentro de um FeedbackProvider');
  }
  return context;
};
